import React from 'react';
import PropTypes from 'prop-types';
import { cn } from 'modules/Common/utils/cn';

/**
 * Tailwind replacement for react-bootstrap's <FloatingLabel> + Form.Control
 * (tailwind-standardization phase 3). Reproduces the Bootstrap 5.3 floating
 * field as it rendered HERE (including the App.scss-era overrides: truncated
 * label, #607489 label color, no label pill background).
 *
 * Usage: the actual <input>/<textarea>/<select> is passed as children and MUST
 * carry `floatingInputClass` (or `floatingSelectClass`) — both include `peer`,
 * and the label's float states key off it. The control must come before the
 * label in the DOM, which this component guarantees.
 *
 * Float semantics mirror Bootstrap exactly: the label floats on focus or when
 * the input is non-empty (`:not(:placeholder-shown)`), which also means a
 * control WITHOUT a placeholder attribute is permanently floated — several
 * legacy form configs rely on that. Selects always float (`alwaysFloat`).
 */

export const floatingInputClass =
  'peer block w-full h-[calc(3.5rem+2px)] rounded-md border border-bsGray-300 bg-white ' +
  'px-3 pt-[1.625rem] pb-[0.625rem] text-base leading-tight ' +
  'placeholder:text-transparent ' +
  'transition-[border-color,box-shadow] duration-150 ease-in-out ' +
  'focus:outline-none focus:border-primaryColor/50 focus:ring-4 focus:ring-primaryColor/25 ' +
  'disabled:bg-bsGray-200';

// Selects keep Bootstrap's chevron + right padding; height/border/focus match
// the input. appearance-none kills the native arrow.
export const floatingSelectClass =
  'peer block w-full h-[calc(3.5rem+2px)] appearance-none rounded-md border border-bsGray-300 bg-white ' +
  'pl-3 pr-9 pt-[1.625rem] pb-[0.625rem] text-base leading-tight ' +
  'transition-[border-color,box-shadow] duration-150 ease-in-out ' +
  'focus:outline-none focus:border-primaryColor/50 focus:ring-4 focus:ring-primaryColor/25 ' +
  'disabled:bg-bsGray-200';

// Bootstrap's select caret, verbatim.
export const floatingSelectCaretStyle = {
  backgroundImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 0.75rem center',
  backgroundSize: '16px 12px',
};

const labelBase =
  'pointer-events-none absolute left-0 top-0 z-[1] h-full w-full overflow-hidden ' +
  'text-ellipsis whitespace-nowrap border border-transparent px-3 py-4 ' +
  'text-[#607489] origin-[0_0] ' +
  'transition-[opacity,transform] duration-100 ease-in-out';

const floated = 'opacity-[.65] scale-[.85] -translate-y-2 translate-x-[0.15rem]';
const floatOnState =
  'peer-focus:opacity-[.65] peer-focus:scale-[.85] peer-focus:-translate-y-2 peer-focus:translate-x-[0.15rem] ' +
  'peer-[:not(:placeholder-shown)]:opacity-[.65] peer-[:not(:placeholder-shown)]:scale-[.85] ' +
  'peer-[:not(:placeholder-shown)]:-translate-y-2 peer-[:not(:placeholder-shown)]:translate-x-[0.15rem]';

export default function CgFloatingField({ id, label, alwaysFloat = false, className, children }) {
  return (
    <div className={cn('relative', className)}>
      {children}
      <label
        htmlFor={id}
        className={cn(labelBase, alwaysFloat ? floated : floatOnState)}
      >
        {label}
      </label>
    </div>
  );
}

CgFloatingField.propTypes = {
  id: PropTypes.string,
  label: PropTypes.node,
  alwaysFloat: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};
